import { CheckRounded } from '@mui/icons-material';
import { Box, Card, CardContent, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AppRoutes } from '../../constants/routes';
import { getPictureURL, StorageType } from '../../helpers/storage';
import { Namespace } from '../../locales/translations';
import { AwarenessCampaignData } from '../../models/AwarenessCampaign';
import { useAppSelector } from '../../store/hooks';
import { isCampaignCompleted } from '../../utils/campaignHelpers';

type AwarenessCampaignCardProps = {
   readonly missionData: AwarenessCampaignData;
};

function AwarenessCampaignCard({ missionData }: AwarenessCampaignCardProps) {
    const { t } = useTranslation([Namespace.ADDRESSES, Namespace.DATES]);
    const navigate = useNavigate();

    const userEmail = useAppSelector(state => state.user.email);

    const handleClick = () => {
        navigate(`/${AppRoutes.AWARENESS_CAMPAIGNS}/${missionData.ID}/${AppRoutes.DETAIL}`);
    };

    const [pictureURL, setPictureURL] = useState<string | null>(null);

    useEffect(() => {
        if (missionData?.mapImageURL) {
            getPictureURL(missionData.mapImageURL, StorageType.AWARENESS_CAMPAIGNS)
                .then((picURL) => {
                    setPictureURL(picURL);
                });
        } else {
            setPictureURL(null);
        }
    }, [missionData?.mapImageURL]);

    const totalAddresses = userEmail ? missionData.assigned[userEmail] : 0;

    const isCompleted = isCampaignCompleted(missionData, userEmail);
    return (
        <Card
            onClick={handleClick}
            sx={{
                cursor: 'pointer',
                boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.20)',
                transition: 'box-shadow 0.3s ease-in-out',
                position: 'relative',
            }}>
            <CardContent>
                <Box
                    position="relative"
                    sx={{
                        height: 180,
                        width: '100%',
                        mb: 2,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        border: '1px solid #ddd',
                        backgroundColor: '#f5f5f5',
                        overflow: 'hidden',
                    }}
                >
                    {pictureURL ? (
                        <Box
                            component="img"
                            sx={{
                                height: "100%",
                                width: '100%',
                                objectFit: 'cover'
                            }}
                            src={pictureURL}
                        />
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            {t('no_address_added', { ns: Namespace.ADDRESSES, })}
                        </Typography>
                    )}
                    
                    {isCompleted && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                height: '100%',
                                width: '100%',
                                backgroundColor: 'rgba(255, 255, 255, 0.80)',
                                display: 'flex',
                                flexDirection: "column",
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <CheckRounded
                                sx={{
                                    color: '#5FB474',
                                    fontSize: 45,
                                }}
                            />
                            <Typography variant="h5" color='#5FB474' sx={{ fontSize: "16", fontWeight: '900', fontStyle: 'normal' }}>
                                {t('completed', { ns: Namespace.COMMONS })}
                            </Typography>
                        </Box>
                    )}
                </Box>
                <Typography variant="h4" paddingBottom={1} margin={0} component="div">
                    {missionData.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {t('created_at', { ns: Namespace.DATES, date: missionData.createdAt })}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                    {t('addresses_to_visit', { ns: Namespace.ADDRESSES, count: totalAddresses })}
                </Typography>
                <Typography variant="h5" color="text.secondary">
                    {t('deadline', { ns: Namespace.DATES, date: missionData.deadline })}
                </Typography>
            </CardContent>
        </Card>
    );
}

export default AwarenessCampaignCard;
